import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { RankRewards } from '../RankRewards';
import { ScoreRewards } from '../ScoreRewards';
import { ThresholdRewards } from '../ThresholdRewards';
import type { LeaderboardRewards_so5Leaderboard } from './__generated__/index.graphql';

type Props = {
  so5Leaderboard: LeaderboardRewards_so5Leaderboard;
  withSeparator?: boolean;
  hideThresholds?: boolean;
  displayAlternativeRewards?: boolean;
} & Pick<
  ComponentProps<typeof RankRewards>,
  'onClickRanking' | 'selectedContender'
>;
export const LeaderboardRewards = ({
  so5Leaderboard,
  withSeparator,
  hideThresholds,
  onClickRanking,
  selectedContender,
  displayAlternativeRewards,
}: Props) => {
  const hasRankRewards = !!(so5Leaderboard.rewardsConfig.ranking || []).length;
  const hasScoreRewards = !!(so5Leaderboard.rewardsConfig.conditional || [])
    .length;
  const hasTargetRewards = !!(so5Leaderboard.rewardsConfig.targetable || [])
    .length;

  const hasMultipleTypeRewards =
    [hasRankRewards, hasScoreRewards, hasTargetRewards].filter(Boolean).length >
    1;

  return (
    <Vertical gap={2}>
      {!!hasTargetRewards && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerTarget} />
            </BodyS>
          )}
          <ScoreRewards
            targetable
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.targetable || []}
            so5League={so5Leaderboard.so5League}
            displayAlternativeRewards={displayAlternativeRewards}
          />
        </>
      )}
      {!!hasScoreRewards && !hasTargetRewards && !hideThresholds && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerThreshold} />
            </BodyS>
          )}
          <ThresholdRewards
            hideCompletionStatus
            defaultRarity={so5Leaderboard.mainRarityType}
            so5RewardConfigs={so5Leaderboard.rewardsConfig.conditional || []}
            so5League={so5Leaderboard.so5League}
            displayAlternativeRewards={displayAlternativeRewards}
          />
        </>
      )}
      {!!hasRankRewards && (
        <>
          {!!hasMultipleTypeRewards && (
            <BodyS bold>
              <FormattedMessage {...fantasy.rewardsPerRank} />
            </BodyS>
          )}
          <RankRewards
            so5Leaderboard={so5Leaderboard}
            withSeparator={withSeparator}
            onClickRanking={onClickRanking}
            selectedContender={selectedContender}
            so5Ranking={so5Leaderboard.mySo5Rankings[0]}
            displayAlternativeRewards={displayAlternativeRewards}
          />
        </>
      )}
    </Vertical>
  );
};

LeaderboardRewards.fragments = {
  so5Leaderboard: gql`
    fragment LeaderboardRewards_so5Leaderboard on So5Leaderboard {
      slug
      so5League {
        slug
        ...ScoreRewards_so5League
        ...ThresholdRewards_so5League
      }
      rewardsConfig {
        ranking {
          id
          ranks
        }
        conditional {
          id
          ...ThresholdRewards_so5RewardConfig
        }
        targetable {
          id
          ...ScoreRewards_so5RewardConfig
        }
        unlockable {
          id
          score
        }
      }
      mySo5Rankings {
        id
        ...RankRewards_so5Ranking
      }
      ...RankRewards_so5Leaderboard
    }
    ${RankRewards.fragments.so5Leaderboard}
    ${RankRewards.fragments.so5Ranking}
    ${ScoreRewards.fragments.so5League}
    ${ThresholdRewards.fragments.so5RewardConfig}
    ${ThresholdRewards.fragments.so5League}
    ${ScoreRewards.fragments.so5RewardConfig}
  ` as TypedDocumentNode<LeaderboardRewards_so5Leaderboard>,
};
